import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import IconWrapper from '../common/IconWrapper';
import { POINT_EVENT, PointRequest } from '../../help/pointer';

/**
 * 왼쪽 영역(탐색기·툴박스) 접기 — 노드 속성(PropertyDock)과 같은 모양: 접으면 얇은 막대만 남고, 누르면 펼친다(2026-10-20).
 *
 *   · 접힘은 보는 사람마다 브라우저에 기억한다(속성 패널의 dockPrefs 와 같은 방식). 저장소를 못 쓰면 펼친 채로 시작한다
 *   · 도움말·AI 가 이 안을 가리키면(toolbox:… / panel:explorer) 저절로 펼친다 — 접힌 채로는 손가락이 갈 곳이 없다
 */
interface Props {
  /** 기억 키·가리키기 판별에 쓴다 */
  id: 'explorer' | 'toolbox';
  /** 접힌 막대에 세로로 적는 이름 */
  label: string;
  children: React.ReactNode;
}

const key = (id: string) => `openflow.side.${id}.collapsed`;
const load = (id: string) => { try { return window.localStorage.getItem(key(id)) === '1'; } catch { return false; } };
const save = (id: string, v: boolean) => { try { window.localStorage.setItem(key(id), v ? '1' : '0'); } catch { /* 저장 못 해도 동작은 같다 */ } };

const CollapsibleSide: React.FC<Props> = ({ id, label, children }) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(() => load(id));
  const set = (v: boolean) => { setCollapsed(v); save(id, v); };

  useEffect(() => {
    const on = (e: Event) => {
      const target = String((e as CustomEvent<PointRequest>).detail?.target || '');
      if ((id === 'toolbox' && target.startsWith('toolbox:')) || (id === 'explorer' && target === 'panel:explorer')) {
        setCollapsed(false);                       // 가리키려고 펼친 것은 기억하지 않는다(사용자가 접어 둔 선택은 그대로)
      }
    };
    window.addEventListener(POINT_EVENT, on);
    return () => window.removeEventListener(POINT_EVENT, on);
  }, [id]);

  if (collapsed) {
    return (
      <button type="button" onClick={() => set(false)} title={t('layout:side.expand', { name: label })}
        className="shrink-0 w-8 h-full flex flex-col items-center gap-2 pt-3 border-r border-gray-200 bg-gray-100 text-xs text-gray-600 hover:bg-gray-200">
        <IconWrapper icon={FiChevronRight} size={16} />
        <span className="[writing-mode:vertical-rl]">{label}</span>
      </button>
    );
  }
  return (
    <div className="relative shrink-0 h-full flex min-h-0">
      {children}
      <button type="button" onClick={() => set(true)} title={t('layout:side.collapse', { name: label })} aria-label={t('layout:side.collapse', { name: label })}
        className="absolute top-2 right-1.5 z-10 p-0.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100">
        <IconWrapper icon={FiChevronLeft} size={14} />
      </button>
    </div>
  );
};

export default CollapsibleSide;
